import {
  Box,
  Dialog,
  DialogContent,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import React, { useMemo, useState } from 'react';

export interface ConsoleCommand {
  id: string;
  label: string;
  detail: string;
  keywords?: string;
  run: () => void;
}

export function CommandPalette({
  open,
  commands,
  onClose,
}: {
  open: boolean;
  commands: ConsoleCommand[];
  onClose: () => void;
}) {
  const [query, setQuery] = useState('');
  const visible = useMemo(() => {
    const term = query.trim().toLowerCase();
    return commands
      .filter(command =>
        `${command.label} ${command.detail} ${command.keywords ?? ''}`
          .toLowerCase()
          .includes(term),
      )
      .slice(0, 25);
  }, [commands, query]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionProps={{ onExited: () => setQuery('') }}
    >
      <DialogContent sx={{ p: 1.5 }}>
        <TextField
          autoFocus
          fullWidth
          size="small"
          placeholder="Search resources or run a command"
          value={query}
          onChange={event => setQuery(event.target.value)}
          onKeyDown={event => {
            if (event.key === 'Enter' && visible[0]) {
              visible[0].run();
              onClose();
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <List dense sx={{ mt: 1, maxHeight: '55vh', overflow: 'auto' }}>
          {visible.map(command => (
            <ListItemButton
              key={command.id}
              onClick={() => {
                command.run();
                onClose();
              }}
              sx={{ borderRadius: 1 }}
            >
              <ListItemText
                primary={command.label}
                secondary={command.detail}
              />
            </ListItemButton>
          ))}
        </List>
        {!visible.length && (
          <Box sx={{ py: 2, textAlign: 'center' }}>
            <Typography color="textSecondary">No commands found.</Typography>
          </Box>
        )}
        <Typography sx={{ px: 1, color: 'text.secondary', fontSize: 10.5 }}>
          Enter to run the first command · Esc to close
        </Typography>
      </DialogContent>
    </Dialog>
  );
}
